<template>
	<div>
		<div v-if="loading" class="text-center py-5">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading…</span>
			</div>
		</div>

		<div v-else-if="error" class="alert alert-danger" role="alert">
			{{ error }}
		</div>

		<div v-else-if="rows.length === 0" class="text-center py-5 text-muted">
			{{ emptyText }}
		</div>

		<template v-else>
			<div class="row g-2 align-items-center mb-3">
				<div class="col-12 col-md-5 col-lg-4">
					<div class="input-group">
						<span class="input-group-text"><i class="bi bi-search"></i></span>
						<input
							id="pesticideSearch"
							v-model="filters.search"
							type="search"
							class="form-control"
							:placeholder="
								variant === 'district'
									? 'Search your products'
									: 'Search product name or active ingredient'
							"
							:aria-label="
								variant === 'district'
									? 'Search your products'
									: 'Search product name or active ingredient'
							"
						/>
					</div>
				</div>
				<div class="col-12 col-md-auto">
					<button
						type="button"
						class="btn btn-outline-secondary"
						:disabled="!hasFilters"
						@click="clearFilters"
					>
						<i class="bi bi-x-circle me-1"></i>Clear filters
					</button>
				</div>
				<div class="col-12 col-md text-md-end text-muted small">
					Showing {{ shown }} of {{ rows.length }} products
				</div>
			</div>

			<!-- The district's own list: what the district holds and the terms it
			     curates. Reference facts stay on the catalog page. -->
			<div v-if="variant === 'district'" class="table-responsive">
				<table class="table table-striped table-hover align-middle">
					<thead class="table-light">
						<tr>
							<th scope="col">Product</th>
							<th scope="col">Brand</th>
							<th scope="col">Catalog</th>
							<th scope="col">Control class</th>
							<th scope="col">Planned residual (days)</th>
							<th scope="col">Target life stages</th>
						</tr>
					</thead>
					<tbody>
						<tr v-if="districtVisible.length === 0">
							<td colspan="6" class="text-center text-muted py-4">
								No products match the current search.
								<button
									type="button"
									class="btn btn-link btn-sm p-0 align-baseline"
									@click="clearFilters"
								>
									Clear filters
								</button>
							</td>
						</tr>
						<tr v-for="row in districtVisible" :key="row.key">
							<td>
								<RouterLink
									class="fw-semibold text-decoration-none"
									:to="row.linkTo"
								>
									{{ row.name }}
								</RouterLink>
								<!-- The catalog entry's own name, shown only where it differs,
								     so the district's stored name is never silently replaced. -->
								<div v-if="row.catalogName" class="small text-muted">
									Catalog: {{ row.catalogName }}
								</div>
							</td>
							<!-- The packaged product on the shelf. It is the brand its
							     catalog entry was matched to; unlinked rows have none. -->
							<td class="small">
								{{ row.brand || "—" }}
							</td>
							<td>
								<button
									v-if="row.catalogUpdateAvailable"
									type="button"
									class="btn btn-sm btn-warning"
									:title="`This row accepted catalog revision ${row.catalogRevision ?? 0}; the catalog is on revision ${row.catalogRevisionCurrent ?? 0}`"
									@click="emit('preview-catalog-update', row.key)"
								>
									<i class="bi bi-arrow-up-circle me-1"></i>Update
								</button>
								<span v-else class="text-muted small">Up to date</span>
							</td>
							<td>
								<select
									class="form-select form-select-sm"
									:value="row.controlClass"
									:aria-label="`Control class for ${row.name}`"
									@change="onClassChange(row, $event)"
								>
									<option v-if="row.controlClass === ''" value="" disabled>
										Not set
									</option>
									<option
										v-for="option in CONTROL_CLASS_OPTIONS"
										:key="option.value"
										:value="option.value"
									>
										{{ option.label }}
									</option>
								</select>
							</td>
							<td>
								<input
									type="number"
									min="0"
									step="1"
									class="form-control form-control-sm"
									:value="row.residualInput"
									:placeholder="
										row.residualSuggested !== null
											? String(row.residualSuggested)
											: undefined
									"
									:aria-label="`Planned residual days for ${row.name}`"
									@change="onResidualChange(row, $event)"
								/>
							</td>
							<td>
								<LifeStageCircles :active="row.targetStages" />
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<!-- The shared catalog: reference facts only, with the district's own
			     in-list state and the add/remove action. -->
			<div v-else class="table-responsive">
				<table class="table table-striped table-hover align-middle">
					<thead class="table-light">
						<tr>
							<th scope="col">
								<button
									type="button"
									class="th-sort"
									@click="toggleSort('name')"
								>
									Product
									<i class="bi" :class="sortIcon('name')"></i>
								</button>
							</th>
							<th scope="col">
								<button
									type="button"
									class="th-sort"
									@click="toggleSort('category')"
								>
									Category
									<i class="bi" :class="sortIcon('category')"></i>
								</button>
							</th>
							<th scope="col">
								<button
									type="button"
									class="th-sort"
									@click="toggleSort('formulation')"
								>
									Formulation
									<i class="bi" :class="sortIcon('formulation')"></i>
								</button>
							</th>
							<th scope="col">Manufacturer</th>
							<th scope="col">
								<button
									type="button"
									class="th-sort"
									@click="toggleSort('activeIngredients')"
								>
									Active Ingredients
									<i class="bi" :class="sortIcon('activeIngredients')"></i>
								</button>
							</th>
							<th scope="col">EPA Registration</th>
							<th scope="col">
								<button
									type="button"
									class="th-sort"
									@click="toggleSort('targets')"
								>
									Targets
									<i class="bi" :class="sortIcon('targets')"></i>
								</button>
							</th>
							<th scope="col">
								<button
									type="button"
									class="th-sort"
									@click="toggleSort('residual')"
								>
									Residual (days)
									<i class="bi" :class="sortIcon('residual')"></i>
								</button>
							</th>
							<!-- Rate Range is deliberately not sortable: its units differ per
							     product (lb, fl oz, gal, pouch, briquet, tablet), so ordering
							     the numbers against each other would be meaningless. -->
							<th scope="col">Rate Range</th>
							<th scope="col">
								<button
									type="button"
									class="th-sort"
									@click="toggleSort('documents')"
								>
									Documents
									<i class="bi" :class="sortIcon('documents')"></i>
								</button>
							</th>
							<th scope="col">District list</th>
						</tr>
						<tr class="filter-row">
							<th scope="col">
								<input
									v-model="filters.name"
									type="text"
									class="form-control form-control-sm"
									aria-label="Filter by product name"
								/>
							</th>
							<th scope="col">
								<select
									v-model="filters.category"
									class="form-select form-select-sm"
									aria-label="Filter by category"
								>
									<option value="">All</option>
									<option
										v-for="option in categoryOptions"
										:key="option"
										:value="option"
									>
										{{ option }}
									</option>
								</select>
							</th>
							<th scope="col">
								<select
									v-model="filters.formulation"
									class="form-select form-select-sm"
									aria-label="Filter by formulation"
								>
									<option value="">All</option>
									<option
										v-for="option in formulationOptions"
										:key="option"
										:value="option"
									>
										{{ option }}
									</option>
								</select>
							</th>
							<th scope="col"></th>
							<th scope="col">
								<input
									v-model="filters.activeIngredients"
									type="text"
									class="form-control form-control-sm"
									aria-label="Filter by active ingredient"
								/>
							</th>
							<th scope="col"></th>
							<th scope="col">
								<select
									v-model="filters.target"
									class="form-select form-select-sm"
									aria-label="Filter by target life stage"
								>
									<option value="">All</option>
									<option
										v-for="target in LIFE_STAGE_TARGETS"
										:key="target.code"
										:value="target.code"
									>
										{{ target.code }} — {{ target.title }}
									</option>
								</select>
							</th>
							<th scope="col">
								<div class="input-group input-group-sm">
									<input
										v-model="filters.residualMin"
										type="number"
										class="form-control"
										placeholder="Min"
										aria-label="Minimum residual days"
									/>
									<input
										v-model="filters.residualMax"
										type="number"
										class="form-control"
										placeholder="Max"
										aria-label="Maximum residual days"
									/>
								</div>
							</th>
							<th scope="col">
								<input
									v-model="filters.rateRange"
									type="text"
									class="form-control form-control-sm"
									aria-label="Filter by rate range"
								/>
							</th>
							<th scope="col">
								<select
									v-model="filters.documents"
									class="form-select form-select-sm"
									aria-label="Filter by documents"
								>
									<option value="any">Any</option>
									<option value="label">Label</option>
									<option value="sds">SDS</option>
									<option value="both">Both</option>
									<option value="none">None</option>
								</select>
							</th>
							<th scope="col"></th>
						</tr>
					</thead>
					<tbody>
						<tr v-if="catalogVisible.length === 0">
							<td colspan="11" class="text-center text-muted py-4">
								No products match the current search and filters.
								<button
									type="button"
									class="btn btn-link btn-sm p-0 align-baseline"
									@click="clearFilters"
								>
									Clear filters
								</button>
							</td>
						</tr>
						<tr v-for="row in catalogVisible" :key="row.key">
							<td>
								<RouterLink
									class="fw-semibold text-decoration-none"
									:to="row.linkTo"
								>
									{{ row.name }}
								</RouterLink>
								<div class="small text-muted font-monospace">
									{{ row.productID }}
								</div>
							</td>
							<td>
								<span
									v-if="row.category"
									class="badge rounded-pill bg-primary fs-6"
									>{{ row.category }}</span
								>
								<span v-else>—</span>
							</td>
							<td>{{ row.formulation || "—" }}</td>
							<td>{{ row.manufacturer || "—" }}</td>
							<td class="text-muted small">
								{{ row.activeIngredients || "—" }}
							</td>
							<td>{{ row.epaRegistration || "—" }}</td>
							<td>
								<LifeStageCircles :active="row.targets" />
							</td>
							<td>{{ row.residualLabel || "—" }}</td>
							<td>{{ row.rateRangeLabel || "—" }}</td>
							<td class="text-nowrap">
								<a
									v-if="row.labelDocumentURL"
									class="btn btn-sm btn-outline-primary me-1"
									:href="row.labelDocumentURL"
									target="_blank"
									rel="noopener"
								>
									Label
								</a>
								<a
									v-if="row.sdsDocumentURL"
									class="btn btn-sm btn-outline-secondary"
									:href="row.sdsDocumentURL"
									target="_blank"
									rel="noopener"
								>
									SDS
								</a>
								<span
									v-if="!row.labelDocumentURL && !row.sdsDocumentURL"
									class="text-muted"
								>
									—
								</span>
							</td>
							<td class="text-nowrap">
								<ButtonLoading
									v-if="row.inDistrict"
									text="Remove"
									icon="bi bi-dash-lg"
									variant="outline-danger"
									size="sm"
									:loading="pendingKey === row.key"
									:disabled="!row.districtProductID"
									@click="
										row.districtProductID &&
										emit('remove', row.districtProductID, row.key)
									"
								/>
								<ButtonLoading
									v-else
									text="Add"
									icon="bi bi-plus-lg"
									variant="outline-primary"
									size="sm"
									:loading="pendingKey === row.key"
									@click="emit('add', row.productID, row.key)"
								/>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</template>
	</div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";

import ButtonLoading from "@/components/common/ButtonLoading.vue";
import LifeStageCircles from "@/components/pesticide/LifeStageCircles.vue";
import type {
	CatalogProductRow,
	DistrictProductRow,
} from "@/components/pesticide/productTable";
import { CONTROL_CLASS_OPTIONS } from "@/lib/controlClass";
import {
	emptyCatalogFilters,
	hasActiveCatalogFilters,
	LIFE_STAGE_TARGETS,
	matchesCatalogFilters,
	sortCatalogProducts,
	type CatalogSortKey,
} from "@/view/configuration/pesticideCatalog";

const props = withDefaults(
	defineProps<{
		rows: (DistrictProductRow | CatalogProductRow)[];
		/** Which page's table this is: the district's own list or the catalog. */
		variant: "district" | "catalog";
		loading?: boolean;
		error?: string;
		emptyText?: string;
		/** The catalog entry whose add/remove is in flight, so its row shows a spinner. */
		pendingKey?: number | null;
	}>(),
	{
		loading: false,
		error: "",
		emptyText: "No products.",
		pendingKey: null,
	},
);

const emit = defineEmits<{
	(e: "update-class", key: number, value: string): void;
	(e: "update-residual", key: number, value: string): void;
	(e: "add", catalogProductID: string, key: number): void;
	(e: "remove", districtProductID: number, key: number): void;
	(e: "preview-catalog-update", key: number): void;
}>();

const filters = reactive(emptyCatalogFilters());
const hasFilters = computed(() =>
	props.variant === "district"
		? filters.search.trim() !== ""
		: hasActiveCatalogFilters(filters),
);

function clearFilters() {
	Object.assign(filters, emptyCatalogFilters());
}

const sortKey = ref<CatalogSortKey | null>(null);
const sortDescending = ref(false);

function toggleSort(key: CatalogSortKey) {
	if (sortKey.value === key) {
		sortDescending.value = !sortDescending.value;
		return;
	}
	sortKey.value = key;
	sortDescending.value = false;
}

function sortIcon(key: CatalogSortKey): string {
	if (sortKey.value !== key) {
		return "bi-arrow-down-up sort-idle";
	}
	return sortDescending.value ? "bi-caret-down-fill" : "bi-caret-up-fill";
}

const formulationOptions = computed(() =>
	Array.from(
		new Set(
			props.rows
				.map((row) => ("formulation" in row ? row.formulation : null))
				.filter((value): value is string => value !== null),
		),
	).sort((a, b) => a.localeCompare(b)),
);
const categoryOptions = computed(() =>
	Array.from(
		new Set(
			props.rows
				.map((row) => ("category" in row ? row.category : ""))
				.filter((value) => value !== ""),
		),
	).sort((a, b) => a.localeCompare(b)),
);

const districtVisible = computed(() =>
	(props.rows as DistrictProductRow[]).filter((row) =>
		matchesCatalogFilters(row.filter, filters),
	),
);

const catalogVisible = computed(() => {
	const matching = (props.rows as CatalogProductRow[]).filter((row) =>
		matchesCatalogFilters(row.filter, filters),
	);
	if (sortKey.value === null) {
		return matching;
	}
	// Reuse the catalog sort over the rows' filter products, then map back.
	const byFilter = new Map(matching.map((row) => [row.filter, row]));
	return sortCatalogProducts(
		matching.map((row) => row.filter),
		sortKey.value,
		sortDescending.value,
	).map((filter) => byFilter.get(filter) as CatalogProductRow);
});

const shown = computed(() =>
	props.variant === "district"
		? districtVisible.value.length
		: catalogVisible.value.length,
);

function onClassChange(row: DistrictProductRow, event: Event) {
	emit("update-class", row.key, (event.target as HTMLSelectElement).value);
}

function onResidualChange(row: DistrictProductRow, event: Event) {
	emit("update-residual", row.key, (event.target as HTMLInputElement).value);
}
</script>

<style scoped>
.table-responsive {
	overflow-x: auto;
}

th {
	white-space: nowrap;
}

/* A header that sorts reads as plain table text until hovered, so the sort
   affordance does not compete with the filter controls below it. */
.th-sort {
	display: inline-flex;
	align-items: center;
	gap: 0.35rem;
	padding: 0;
	border: 0;
	background: none;
	font: inherit;
	font-weight: 600;
	color: inherit;
}

.th-sort:hover {
	text-decoration: underline;
}

.sort-idle {
	opacity: 0.25;
}

.filter-row th {
	padding-top: 0.25rem;
}
</style>
